import { useEffect, useRef, useState } from 'react';
import { bridgePositions } from '../games/bridge-nt';
import type { Card, Seat } from '../games/heads-up-poker';
import { useRoom } from '../state/room';
import { canUseMpsFinalDeck, isMpsContext, mpsIdentity, mpsStorageKey, partialContext, pokerBoardTargets, revealForPositions, shuffleContext } from './mps-context';
import { cardIndexToRankSuit, combinePks, ensureMentalPoker, finalizeCard, initialDeck, type KeyPair, keypairFromSeed, type PartialShare, partialDecryptWithProof, shuffleDeckWithProof, verifyPartial, verifyShuffle } from './poker-crypto';

export type Role = 'host' | 'guest' | 'spectator';
const SB_HOLE = [0, 1] as const;
const BB_HOLE = [2, 3] as const;
const BOARD_ORDER = [4, 5, 6, 7, 8] as const;
interface InternalState {
  identity: string;
  running: boolean;
  pending: boolean;
  keypair: KeyPair | null;
  pkCombined: string | null;
  finalDeck: string[] | null;
  ownPartials: Map<number, PartialShare>;
  cards: Map<number, Card>;
  sentOps: Set<string>;
  retryOps: Map<string, () => void>;
  verifiedShuffle: boolean;
  failed: boolean;
}
function createInternal(identity: string): InternalState {
  return {identity, running: false, pending: false, keypair: null, pkCombined: null, finalDeck: null, ownPartials: new Map(), cards: new Map(), sentOps: new Set(), retryOps: new Map(), verifiedShuffle: false, failed: false};
}
function bytesToHex(bytes: Uint8Array): string { return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(''); }
function hexToBytes(hex: string): Uint8Array { return Uint8Array.from(hex.match(/../g) ?? [], byte => Number.parseInt(byte, 16)); }
function randomHex32(): string { return bytesToHex(crypto.getRandomValues(new Uint8Array(32))); }
function persistedSeed(key: string): string {
  try { const existing = localStorage.getItem(key); if (existing && /^[0-9a-fA-F]{64}$/.test(existing)) return existing; } catch { /* in-memory operation remains available */ }
  const seed = randomHex32();
  try { localStorage.setItem(key, seed); } catch { /* current deal continues */ }
  return seed;
}
function persistedNonces(key: string): {perm: string; rerand: string; proof: string} {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (parsed && [parsed.perm, parsed.rerand, parsed.proof].every(value => typeof value === 'string' && /^[0-9a-fA-F]{64}$/.test(value))) return parsed;
  } catch { /* fresh nonce set for this session */ }
  const nonces = {perm: randomHex32(), rerand: randomHex32(), proof: randomHex32()};
  try { localStorage.setItem(key, JSON.stringify(nonces)); } catch { /* current deal continues */ }
  return nonces;
}

/** Secret keys stay in this hook. Recipient-local proofs are used only to submit that recipient's cards. */
export function useMentalPokerHand(params: {
  board: number | null;
  hand: number;
  role: Role | null;
  mySeat: Seat | null;
  bridgeSide?: 'First' | 'Second';
  awaiting: string | null;
  handPhase?: string | null;
  matchCode: string;
  engineMyHole: [Card, Card] | null;
  engineBoard: Card[];
  active: boolean;
}): void {
  const {board, hand, role, mySeat, bridgeSide, awaiting, matchCode, active} = params;
  const entry = useRoom(state => board == null ? null : state.mps.get(`${board}:${hand}`) ?? null);
  const context = entry?.context ?? null;
  const fingerprint = `${entry?.hostPublicKey ? 1 : 0}:${entry?.guestPublicKey ? 1 : 0}:${entry?.shuffleRound1Deck ? 1 : 0}:${entry?.finalDeck ? 1 : 0}:${entry?.partialByIndex.size ?? 0}`;
  const identity = board != null && context && role ? mpsIdentity(matchCode, board, hand, context, role) : null;
  const internal = useRef<InternalState | null>(null);
  // Reset immediately during identity changes, before an old await continuation can publish.
  if (internal.current?.identity !== identity) {
    internal.current = identity ? createInternal(identity) : null;
  }
  const engineBoardRef = useRef(params.engineBoard);
  engineBoardRef.current = params.engineBoard;
  const engineBoardLength = params.engineBoard.length;
  const activeRef = useRef(active);
  activeRef.current = active;
  const [trigger, setTrigger] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: restore initial session during StrictMode effect replay only
  useEffect(() => {
    if (identity && !internal.current) internal.current = createInternal(identity);
    return () => { internal.current = null; };
  }, []);

  useEffect(() => {
    const session = internal.current;
    if (!active || !session || board == null || !context || !role || role === 'spectator') return;
    const timer = setInterval(() => {
      const state = useRoom.getState();
      const current = state.mps.get(`${board}:${hand}`);
      if (internal.current !== session || session.failed || state.code !== matchCode || state.you !== role || current?.context !== context || !activeRef.current) return;
      for (const [op, publish] of session.retryOps) {
        if (op === 'key' && (role === 'host' ? current.hostPublicKey : current.guestPublicKey)) continue;
        if (op === 'shuffle' && (role === 'host' ? current.shuffleRound1Deck : current.finalDeck)) continue;
        publish();
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [active, board, hand, context, role, matchCode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: primitive protocol events intentionally resume the run
  useEffect(() => {
    const session = internal.current;
    if (!active || !session || board == null || !isMpsContext(context) || !role || role === 'spectator' || mySeat == null) return;
    if (session.running) { session.pending = true; return; }
    const myRole = role;
    const oppositeRole = role === 'host' ? 'guest' : 'host';
    const current = () => useRoom.getState().mps.get(`${board}:${hand}`);
    const alive = () => internal.current === session && activeRef.current && useRoom.getState().code === matchCode && useRoom.getState().you === role && current()?.context === context;
    const publishResults = (update: Parameters<ReturnType<typeof useRoom.getState>['setMpsLocalResults']>[3]) => {
      if (alive()) useRoom.getState().setMpsLocalResults(board, hand, context, update);
    };
    const rememberSend = (op: string, send: () => void) => {
      const publish = () => { if (alive() && !session.failed) send(); };
      session.sentOps.add(op);
      session.retryOps.set(op, publish);
      publish();
    };
    const run = async () => {
      if (session.failed || !alive()) return;
      await ensureMentalPoker();
      if (!alive()) return;
      if (!session.keypair) {
        const keypair = await keypairFromSeed(persistedSeed(mpsStorageKey('seed', matchCode, board, hand, context, myRole)));
        if (!alive()) return;
        session.keypair = keypair;
        rememberSend('key', () => useRoom.getState().sendMpsKeyShare(board, hand, context, hexToBytes(keypair.pk)));
      }
      const keys = current();
      if (!keys?.hostPublicKey || !keys.guestPublicKey) return;
      const hostPk = bytesToHex(keys.hostPublicKey);
      const guestPk = bytesToHex(keys.guestPublicKey);
      if (session.keypair.pk !== (myRole === 'host' ? hostPk : guestPk)) throw new Error('配牌の鍵が一致しません');
      const oppositePk = myRole === 'host' ? guestPk : hostPk;
      if (!session.pkCombined) {
        const combined = await combinePks([hostPk, guestPk]);
        if (!alive()) return;
        session.pkCombined = combined;
      }
      const combined = session.pkCombined;
      // The opaque 32-byte context is already the initial deck seed; do not hash it again.
      const nonces = persistedNonces(mpsStorageKey('shuffle', matchCode, board, hand, context, myRole));
      if (myRole === 'host' && !session.sentOps.has('shuffle')) {
        const base = await initialDeck(combined, context);
        if (!alive()) return;
        const shuffled = await shuffleDeckWithProof(base, combined, nonces.perm, nonces.rerand, nonces.proof, shuffleContext(context, 'host'));
        if (!alive()) return;
        rememberSend('shuffle', () => useRoom.getState().sendMpsShuffle(board, hand, context, shuffled.deck, shuffled.proof));
      }
      const round1 = current()?.shuffleRound1Deck;
      const proof1 = current()?.shuffleRound1Proof;
      if (myRole === 'guest' && round1 && proof1 && !session.verifiedShuffle) {
        const base = await initialDeck(combined, context);
        if (!alive()) return;
        const valid = await verifyShuffle(base, round1, combined, proof1, shuffleContext(context, 'host'));
        if (!alive()) return;
        if (!valid) { session.failed = true; publishResults({cheatDetected: true}); return; }
        session.verifiedShuffle = true;
      }
      if (myRole === 'guest' && round1 && session.verifiedShuffle && !session.sentOps.has('shuffle')) {
        const shuffled = await shuffleDeckWithProof(round1, combined, nonces.perm, nonces.rerand, nonces.proof, shuffleContext(context, 'guest'));
        if (!alive()) return;
        session.finalDeck = [...shuffled.deck];
        rememberSend('shuffle', () => useRoom.getState().sendMpsShuffle(board, hand, context, shuffled.deck, shuffled.proof));
      }
      const final = current()?.finalDeck;
      const proof2 = current()?.finalDeckProof;
      if (!final || !round1 || (myRole === 'host' && !proof2)) return;
      if (myRole === 'host' && !session.verifiedShuffle) {
        const valid = await verifyShuffle(round1, final, combined, proof2!, shuffleContext(context, 'guest'));
        if (!alive()) return;
        if (!valid) { session.failed = true; publishResults({cheatDetected: true}); return; }
        session.verifiedShuffle = true;
      }
      if (!canUseMpsFinalDeck(myRole, session.verifiedShuffle, final, proof2 ?? null, session.finalDeck)) return;
      if (myRole === 'host') session.finalDeck = final;
      const ownPartial = async (position: number): Promise<PartialShare> => {
        const cached = session.ownPartials.get(position);
        if (cached) return cached;
        const result = await partialDecryptWithProof(final[position], session.keypair!.sk, partialContext(context, myRole, position));
        if (alive()) session.ownPartials.set(position, result);
        return result;
      };
      const myPositions = bridgeSide ? bridgePositions(bridgeSide) : mySeat === 'sb' ? SB_HOLE : BB_HOLE;
      const oppositePositions = bridgeSide ? bridgePositions(bridgeSide === 'First' ? 'Second' : 'First') : mySeat === 'sb' ? BB_HOLE : SB_HOLE;
      const sendPartials = async (op: string, positions: readonly number[]) => {
        if (session.sentOps.has(op)) return;
        const partials: PartialShare[] = [];
        for (const position of positions) {
          partials.push(await ownPartial(position));
          if (!alive()) return;
        }
        rememberSend(op, () => useRoom.getState().sendMpsPartialDecrypt(board, hand, context, [...positions], partials.map(partial => partial.share), partials.map(partial => partial.proof)));
      };
      await sendPartials('holes', oppositePositions);
      if (!alive()) return;
      const targets = bridgeSide ? [] : pokerBoardTargets(awaiting);
      if (targets.length) {
        await sendPartials(`board:${targets.join(',')}`, targets);
        if (!alive()) return;
      }
      // Public engine cards form the trusted prefix on reload; new cards still require both proofs.
      if (!bridgeSide) engineBoardRef.current.forEach((card, index) => { session.cards.set(4 + index, card); });
      // Opposite private positions are never finalized or copied into the recipient store.
      for (const position of [...myPositions, ...targets]) {
        if (session.cards.has(position)) continue;
        const opposite = current()?.partialByIndex.get(position)?.[oppositeRole];
        if (!opposite) continue;
        const valid = await verifyPartial(final[position], oppositePk, opposite.share, opposite.proof, partialContext(context, oppositeRole, position));
        if (!alive()) return;
        if (!valid) { session.failed = true; publishResults({cheatDetected: true}); return; }
        const own = await ownPartial(position);
        if (!alive()) return;
        const card = await finalizeCard(final[position], [own.share, opposite.share]);
        if (!alive()) return;
        if (card == null) throw new Error('配牌を復号できません');
        session.cards.set(position, cardIndexToRankSuit(card));
      }
      if (myPositions.every(position => session.cards.has(position))) {
        const reveal = revealForPositions(context, session.ownPartials, myPositions);
        if (!reveal) return;
        const cards = myPositions.map(position => session.cards.get(position)!);
        if (bridgeSide) {
          if (!current()?.bridgeReveal) publishResults({bridgeHands: [cards.slice(0, 13), cards.slice(13)], bridgeReveal: reveal, cryptoReady: true});
        } else if (!current()?.holeReveal) publishResults({myHole: [cards[0], cards[1]], holeReveal: reveal, cryptoReady: true});
      }
      if (!bridgeSide) {
        const revealedBoard: Card[] = [];
        const boardRevealByIndex = new Map<number, PartialShare>();
        for (const position of BOARD_ORDER) {
          const card = session.cards.get(position);
          if (!card) break;
          revealedBoard.push(card);
          const partial = session.ownPartials.get(position);
          if (partial) boardRevealByIndex.set(position, partial);
        }
        if (revealedBoard.length > (current()?.revealedBoard.length ?? 0)) publishResults({revealedBoard, boardRevealByIndex});
      }
    };
    session.running = true;
    void run().catch(() => {
      if (alive()) { session.failed = true; publishResults({cryptoError: true}); }
    }).finally(() => {
      session.running = false;
      if (alive() && session.pending) { session.pending = false; setTrigger(value => value + 1); }
    });
  }, [active, board, hand, context, role, mySeat, bridgeSide, awaiting, matchCode, fingerprint, trigger, engineBoardLength]);
}
